import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import { ODOO_COMPANY_IDS } from "AppSettings";
import Button from "components/base/Button";
import StateCardView from "components/base/StateCardView";
import { Alert } from "components/base/Toast";
import OdooVendorsView from "components/shared/OdooVendorsView";
import { useModals } from "ModalProvider";
import { OdooContext } from "OdooProvider";
import { useContext } from "react";
import { BsTrash } from "react-icons/bs";
import {
  CREATE_SUPPLIER_COMPANY,
  DELETE_SUPPLIER_COMPANY,
} from "./release/graphql";

const ManageSupplierView = ({ supplier }) => {
  const modal = useModals();
  const { odooUrl } = useContext(OdooContext);
  const [createSupplierCompany, createSupplierCompanyRes] = useMutation(
    CREATE_SUPPLIER_COMPANY,
    {
      onError(res) {
        Alert("error", parseError(res));
      },
      onCompleted() {
        Alert("success", "Successfully connected to Odoo Vendor");
      },
    },
  );
  const [deleteSupplierCompany, deleteSupplierCompanyRes] = useMutation(
    DELETE_SUPPLIER_COMPANY,
    {
      onError(res) {
        Alert("error", parseError(res));
      },
      onCompleted() {
        Alert("success", "Successfully disconnected from Odoo Vendor");
      },
    },
  );

  function releaseHandler() {
    modal.present({
      title: "Connect to Odoo Vendor",
      children: <OdooVendorsView onSelect={onSelectOdooVendor} />,
    });
  }

  function onSelectOdooVendor(company, odooVendor) {
    if (
      window.confirm(`Are you sure to connect to ${odooVendor.name} in Odoo?`)
    ) {
      modal.hide();
      createSupplierCompany({
        variables: {
          supplierId: supplier.id,
          system: company,
          odooPartnerId: odooVendor.id,
        },
      });
    }
  }

  function deleteHandler(supplierCompany) {
    if (
      window.confirm(
        `Are you sure you want to disconnect from Odoo #${supplierCompany.odooPartnerId}?`,
      )
    ) {
      deleteSupplierCompany({
        variables: {
          supplierId: supplier.id,
          supplierCompanyId: supplierCompany.id,
        },
        optimisticResponse: {
          deleteSupplierCompany: {
            __typename: "DeleteSupplierCompany",
            supplier: {
              __typename: "Supplier",
              id: supplier.id,
              companies: supplier.companies.filter(
                (i) => i.id !== supplierCompany.id,
              ),
            },
          },
        },
      });
    }
  }

  return (
    <StateCardView
      primary={
        supplier.odooPartners.length === 0 && supplier.state === "VERIFIED"
      }
    >
      <h5>System Companies</h5>

      <div className="mt-2">
        {supplier.odooPartners.length > 0 && (
          <div className="-mx-2 text-xs">
            <table>
              <tbody>
                {supplier.odooPartners.map((i) => (
                  <tr
                    key={i.id}
                    className="border-y border-gray-100 dark:border-gray-700"
                  >
                    <td>
                      <a
                        className="text-purple-600"
                        href={`${odooUrl}/odoo/vendors/${i.odooId}?cids=${
                          ODOO_COMPANY_IDS[i.company]
                        }`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        [{i.company}] Odoo # {i.odooId}
                      </a>
                    </td>
                    <td>
                      <div className="flex justify-end">
                        <Button
                          danger
                          onClick={() => deleteHandler(i)}
                          loading={deleteSupplierCompanyRes.loading}
                        >
                          <BsTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-2">
          <div className=" opacity-70 text-xs">
            You can connect to multiple Odoo Vendors in different systems.
          </div>

          <Button
            className="mt-1"
            odoo
            onClick={releaseHandler}
            loading={createSupplierCompanyRes.loading}
          >
            Connect to Odoo Vendor
          </Button>
        </div>

        <div className="text-xs mt-2 border-t pt-2">
          <div>Supplier company information is managed in Odoo.</div>

          <div className="mt-1 flex flex-col">
            <a
              href={`${odooUrl}/odoo/vendors?cids=${ODOO_COMPANY_IDS["AB"]}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600"
            >
              Go to AB Odoo Vendors Page
            </a>
            <a
              href={`${odooUrl}/odoo/vendors?cids=${ODOO_COMPANY_IDS["LTD"]}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600"
            >
              Go to LTD Odoo Vendors Page
            </a>
          </div>
        </div>
      </div>
    </StateCardView>
  );
};

export default ManageSupplierView;
